import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const ParticleBackground = ({ theme }) => {
    const mountRef = useRef(null);

    useEffect(() => {
        let width = window.innerWidth;
        let height = window.innerHeight;

        // Colors based on theme
        const isDark = theme !== 'light';
        const bgColor = isDark ? 0x121212 : 0xf0f2f5;
        const particleColor = isDark ? 0x00f2ff : 0x007bff;
        const lineColor = isDark ? 0x7000ff : 0x6610f2;

        // Scene setup
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(bgColor);

        // Camera setup
        const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        camera.position.z = 100;

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(window.devicePixelRatio);

        // Check if mountRef.current is present before appending
        if (mountRef.current) {
            // Clear previous canvas if any
            while (mountRef.current.firstChild) {
                mountRef.current.removeChild(mountRef.current.firstChild);
            }
            mountRef.current.appendChild(renderer.domElement);
        }

        // Particles setup
        const particleCount = 150;
        const particles = new THREE.BufferGeometry();
        const positions = new Float32Array(particleCount * 3);
        const velocities = [];

        for (let i = 0; i < particleCount; i++) {
            positions[i * 3] = (Math.random() - 0.5) * 400; // x
            positions[i * 3 + 1] = (Math.random() - 0.5) * 400; // y
            positions[i * 3 + 2] = (Math.random() - 0.5) * 200; // z

            velocities.push({
                x: (Math.random() - 0.5) * 0.2,
                y: (Math.random() - 0.5) * 0.2,
                z: (Math.random() - 0.5) * 0.2
            });
        }

        particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // Particle Material
        const particleMaterial = new THREE.PointsMaterial({
            color: particleColor,
            size: 1.5,
            transparent: true,
            opacity: 0.8
        });

        const particleSystem = new THREE.Points(particles, particleMaterial);
        scene.add(particleSystem);

        // Lines setup
        const lineMaterial = new THREE.LineBasicMaterial({
            color: lineColor,
            transparent: true,
            opacity: 0.15
        });

        const linesGeometry = new THREE.BufferGeometry();
        const lines = new THREE.LineSegments(linesGeometry, lineMaterial);
        scene.add(lines);

        // Animation Loop
        const animate = () => {
            requestAnimationFrame(animate);

            const positions = particleSystem.geometry.attributes.position.array;

            // Update positions
            for (let i = 0; i < particleCount; i++) {
                // Update individual particle based on velocity
                positions[i * 3] += velocities[i].x;
                positions[i * 3 + 1] += velocities[i].y;
                positions[i * 3 + 2] += velocities[i].z;

                // Bounce off walls (simple boundary check)
                if (positions[i * 3] > 200 || positions[i * 3] < -200) velocities[i].x *= -1;
                if (positions[i * 3 + 1] > 200 || positions[i * 3 + 1] < -200) velocities[i].y *= -1;
                if (positions[i * 3 + 2] > 100 || positions[i * 3 + 2] < -100) velocities[i].z *= -1;
            }

            particleSystem.geometry.attributes.position.needsUpdate = true;

            // Connect particles with lines if they are close
            updateLines(positions);

            // Rotate system slightly
            particleSystem.rotation.y += 0.0005;
            lines.rotation.y += 0.0005;

            renderer.render(scene, camera);
        };

        const updateLines = (positions) => {
            const linePositions = [];
            const connectDistance = 40;

            for (let i = 0; i < particleCount; i++) {
                for (let j = i + 1; j < particleCount; j++) {
                    const dx = positions[i * 3] - positions[j * 3];
                    const dy = positions[i * 3 + 1] - positions[j * 3 + 1];
                    const dz = positions[i * 3 + 2] - positions[j * 3 + 2];
                    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (dist < connectDistance) {
                        linePositions.push(
                            positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2],
                            positions[j * 3], positions[j * 3 + 1], positions[j * 3 + 2]
                        );
                    }
                }
            }
            lines.geometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
        };

        animate();

        // Handle Resize
        const handleResize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            renderer.setSize(width, height);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
        };

        window.addEventListener('resize', handleResize);

        // transform return cleanup function
        return () => {
            window.removeEventListener('resize', handleResize);
            if (mountRef.current && renderer.domElement) {
                mountRef.current.removeChild(renderer.domElement);
            }
            // Dispose geometries and materials
            particles.dispose();
            particleMaterial.dispose();
            linesGeometry.dispose();
            lineMaterial.dispose();
        }

    }, [theme]);

    return <div ref={mountRef} style={{ position: 'fixed', top: 0, left: 0, zIndex: -1, width: '100%', height: '100%', pointerEvents: 'none' }} />;
};

export default ParticleBackground;
